import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/auth";

/* /smartket/slips — Dashboard showing all slips a user is part of,
   both as marketer and as founder. Founder can counter-sign pending
   slips right here. */

const STATUS_STYLE = {
  pending_founder: "bg-[#d4a34a]/20 text-[#d4a34a] border-[#d4a34a]/40",
  active: "bg-[#3d7a4f]/20 text-[#7fd49b] border-[#3d7a4f]",
  expired: "bg-white/10 text-white/60 border-white/20",
  declined: "bg-yt-red/10 text-yt-red border-yt-red/40",
  terminated: "bg-yt-red/10 text-yt-red border-yt-red/40",
};

export default function SmartKetMySlips() {
  const { user } = useAuth();
  const [sp] = useSearchParams();
  const justCreated = sp.get("created");
  const [asMarketer, setAsMarketer] = useState([]);
  const [asFounder, setAsFounder] = useState([]);
  const [loading, setLoading] = useState(true);
  const [terms, setTerms] = useState(null);
  const [signing, setSigning] = useState(null); // slip id being signed
  const [sig, setSig] = useState("");
  const [agree, setAgree] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => { document.title = "SmartKet — My Slips"; }, []);

  useEffect(() => {
    if (!user) return;
    load();
    axios.get(`${API}/smartket/terms`).then(r => setTerms(r.data));
  }, [user]);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/smartket/slips/mine`);
      setAsMarketer(r.data?.as_marketer || []);
      setAsFounder(r.data?.as_founder || []);
    } catch (_e) {}
    setLoading(false);
  };

  const sign = async (slipId) => {
    setErr("");
    if (!sig.trim() || !agree || !terms) { setErr("agree and type your name to sign"); return; }
    try {
      await axios.post(`${API}/smartket/slips/founder-sign`, {
        slip_id: slipId,
        signature_text: sig.trim(),
        accepted_terms_version: terms.version,
      });
      setSigning(null); setSig(""); setAgree(false);
      await load();
    } catch (e2) {
      setErr(e2.response?.data?.detail || "couldn't sign the slip");
    }
  };

  const decline = async (slipId) => {
    if (!window.confirm("Decline this slip? The marketer will be notified.")) return;
    setErr("");
    try {
      await axios.post(`${API}/smartket/slips/${slipId}/decline`);
      await load();
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "couldn't decline the slip");
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-[#0d1410] text-white p-8 max-w-lg mx-auto">
        <div className="font-mono text-sm">Sign in to view your slips.</div>
      </div>
    );
  }

  return (
    <div data-testid="myslips-page" className="min-h-screen bg-[#0d1410] text-white">
      <div className="border-b-2 border-[#d4a34a]/40 bg-[#141a15]">
        <div className="max-w-5xl mx-auto px-4 md:px-8 py-8">
          <Link to="/smartket/join" className="font-mono text-[10px] tracking-[0.4em] text-white/60 hover:text-[#d4a34a]">
            ← SMARTKET
          </Link>
          <h1 className="mt-3 font-display text-4xl md:text-5xl leading-none tracking-tight">
            MY <span className="text-[#d4a34a]">SLIPS</span>
          </h1>
          <p className="mt-3 font-mono text-sm text-white/75 max-w-xl">
            Every active handshake between you and someone else on SmartKet lives here.
          </p>
          {justCreated && (
            <div className="mt-4 font-mono text-xs text-[#7fd49b] border border-[#3d7a4f]/50 bg-[#3d7a4f]/10 p-3">
              Slip sent to the founder. You'll see it activate here once they counter-sign.
            </div>
          )}
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 md:px-8 py-8 space-y-10">
        {loading && <div className="font-mono text-sm text-white/60">loading…</div>}

        {/* STRIPE CONNECT — payout onboarding banner. Shown when the
            user hasn't finished Stripe onboarding, hidden once payouts
            are enabled. Founders + marketers both see this because
            SmartKet is merchant of record and both need a Stripe
            recipient account to receive money. */}
        {!loading && user && !user.stripe_payouts_enabled && (
          <StripeConnectBanner
            role={user.stripe_role || (asMarketer.length ? "marketer" : "founder")}
            hasAccount={!!user.stripe_account_id}
          />
        )}
        {!loading && user && user.stripe_payouts_enabled && (
          <div data-testid="stripe-ready-banner"
               className="border-2 border-[#3d7a4f] bg-[#3d7a4f]/15 p-3 flex items-center gap-3 font-mono text-xs">
            <span className="text-[#7fd49b] text-lg leading-none">✓</span>
            <span className="text-white/90">
              PAYOUTS READY — Stripe is connected. Bi-weekly transfers fire automatically on verified sales.
            </span>
          </div>
        )}

        {/* FOUNDER SIDE — pending sign-offs go here */}
        {!loading && (
          <section data-testid="slips-as-founder">
            <div className="font-mono text-[10px] tracking-[0.4em] text-[#d4a34a] mb-3">SLIPS ON YOUR PRODUCTS</div>
            {asFounder.length === 0 && (
              <div className="border border-dashed border-white/20 p-6 font-mono text-xs text-white/60">
                No marketer has pulled a slip on your listings yet. Post something on the <Link to="/smartket/board" className="text-[#d4a34a] underline">board</Link>.
              </div>
            )}
            <div className="space-y-3">
              {asFounder.map(s => (
                <SlipCard key={s.id} slip={s} viewerSide="founder">
                  {s.status === "pending_founder" && (
                    <div className="mt-3 pt-3 border-t border-white/10 space-y-2">
                      {signing === s.id ? (
                        <>
                          <div className="font-mono text-[10px] tracking-widest text-[#d4a34a]">COUNTER-SIGN AS FOUNDER</div>
                          <label className="flex items-start gap-2 cursor-pointer">
                            <input type="checkbox" checked={agree} onChange={e => setAgree(e.target.checked)}
                                   data-testid={`agree-${s.id}`} className="mt-1 accent-[#d4a34a]" />
                            <span className="font-mono text-[11px] text-white/80">
                              I've read the terms (<Link to="/smartket/terms" target="_blank" className="text-[#d4a34a] underline">read again</Link>) and I agree to pay <b className="text-[#d4a34a]">{s.commission_pct}%</b> on verified sales attributed to <b>{s.marketer_name}</b> under the <b>{s.template}</b> template.
                            </span>
                          </label>
                          <input value={sig} onChange={e => setSig(e.target.value)} placeholder="Type your full legal name"
                                 data-testid={`sig-${s.id}`}
                                 className="w-full bg-black/40 border border-white/20 focus:border-[#d4a34a] px-3 py-2 text-white text-base font-display italic outline-none" />
                          {err && <div className="font-mono text-xs text-yt-red">{err}</div>}
                          <div className="flex gap-2">
                            <button onClick={() => sign(s.id)} data-testid={`countersign-${s.id}`}
                                    disabled={!agree || !sig.trim()}
                                    className="bg-[#d4a34a] text-black font-display text-sm tracking-widest px-4 py-2 shadow-brutal hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed">
                              ACTIVATE SLIP
                            </button>
                            <button onClick={() => { setSigning(null); setSig(""); setAgree(false); setErr(""); }}
                                    className="bg-transparent border border-white/20 hover:bg-white/10 font-mono text-xs tracking-widest px-3 py-2">
                              CANCEL
                            </button>
                          </div>
                        </>
                      ) : (
                        <div className="flex gap-2 flex-wrap">
                          <button onClick={() => { setSigning(s.id); setSig(""); setAgree(false); setErr(""); }}
                                  data-testid={`open-sign-${s.id}`}
                                  className="bg-[#d4a34a] text-black font-display text-sm tracking-widest px-4 py-2 shadow-brutal hover:bg-white">
                            ▸ REVIEW & COUNTER-SIGN
                          </button>
                          <button onClick={() => decline(s.id)} data-testid={`decline-${s.id}`}
                                  className="bg-transparent border border-yt-red/50 text-yt-red hover:bg-yt-red/10 font-mono text-xs tracking-widest px-3 py-2">
                            DECLINE
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </SlipCard>
              ))}
            </div>
          </section>
        )}

        {/* MARKETER SIDE */}
        {!loading && (
          <section data-testid="slips-as-marketer">
            <div className="font-mono text-[10px] tracking-[0.4em] text-[#d4a34a] mb-3">SLIPS YOU PULLED</div>
            {asMarketer.length === 0 && (
              <div className="border border-dashed border-white/20 p-6 font-mono text-xs text-white/60">
                You haven't pulled any slips yet. Head to the <Link to="/smartket/board" className="text-[#d4a34a] underline">board</Link> and find a product you can move.
              </div>
            )}
            <div className="space-y-3">
              {asMarketer.map(s => <SlipCard key={s.id} slip={s} viewerSide="marketer" />)}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}

function SlipCard({ slip, viewerSide, children }) {
  const styles = STATUS_STYLE[slip.status] || STATUS_STYLE.expired;
  return (
    <div data-testid={`slip-${slip.id}`} className="border-2 border-white/10 bg-[#141a15] p-4 md:p-5">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="font-mono text-[10px] tracking-widest text-white/50 mb-1">
            {viewerSide === "founder" ? `MARKETER · ${slip.marketer_name}` : `FOUNDER · ${slip.founder_name}`}
          </div>
          <div className="font-display text-xl leading-tight">{slip.listing_title}</div>
          <div className="mt-1 font-mono text-[11px] text-white/60">code: <span className="text-[#d4a34a]">{slip.attribution_code}</span></div>
        </div>
        <span className={`font-mono text-[10px] tracking-widest px-2 py-1 border ${styles}`}>
          {slip.status.replace("_", " ").toUpperCase()}
        </span>
      </div>
      <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2 font-mono text-[11px] text-white/70">
        <Stat label="COMMISSION" value={`${slip.commission_pct}%`} />
        <Stat label="TEMPLATE" value={slip.template} />
        <Stat label="ATTRIB. WINDOW" value={`${slip.attribution_days}d`} />
        <Stat label="TERMS" value={`v${slip.terms_version}`} />
      </div>
      <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-2 font-mono text-[11px] text-white/70">
        <Stat label="SALES" value={slip.sales_count || 0} />
        <Stat label="EARNED" value={`$${(slip.commission_earned || 0).toFixed(2)}`} />
        <Stat label="GROSS" value={`$${(slip.gross_revenue || 0).toFixed(2)}`} />
      </div>
      {children}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-black/40 p-2 border border-white/10">
      <div className="text-[9px] tracking-widest text-white/40">{label}</div>
      <div className="text-white/90">{value}</div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────
   STRIPE CONNECT ONBOARDING BANNER
   Sept 19 2026 — Paul finished setting up his sandbox tonight.
   Endpoint on the backend takes a role + returns a hosted Stripe
   URL. We open it in a new tab so the user can complete the
   Express onboarding flow without losing this dashboard.
   ────────────────────────────────────────────────────────────── */
function StripeConnectBanner({ role, hasAccount }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const connect = async () => {
    setErr(""); setBusy(true);
    try {
      const endpoint = role === "founder"
        ? "/smartket/stripe/onboard-founder"
        : "/smartket/stripe/onboard-marketer";
      const { data } = await axios.post(`${API}${endpoint}`, { country: "US" });
      if (data?.url) {
        window.open(data.url, "_blank", "noopener,noreferrer");
      } else {
        setErr("no url returned — try again");
      }
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "couldn't create Stripe link");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div data-testid="stripe-connect-banner"
         className="border-2 border-[#d4a34a] bg-[#d4a34a]/10 p-4 md:p-5 flex items-start justify-between gap-3 flex-wrap">
      <div className="flex-1 min-w-[240px]">
        <div className="font-mono text-[10px] tracking-[0.4em] text-[#d4a34a] mb-2">
          {hasAccount ? "▸ FINISH STRIPE ONBOARDING" : "▸ CONNECT STRIPE TO GET PAID"}
        </div>
        <div className="font-mono text-sm text-white/85 leading-relaxed">
          {hasAccount
            ? "You started onboarding but haven't finished. Payouts are on hold until Stripe is fully set up."
            : "SmartKet pays out via Stripe. Connect once, then verified sales route to your account automatically — first sale releases immediately, everything after runs bi-weekly."}
        </div>
        {err && <div className="mt-2 font-mono text-xs text-yt-red">{err}</div>}
      </div>
      <button
        type="button"
        onClick={connect}
        disabled={busy}
        data-testid="stripe-connect-btn"
        className="bg-[#d4a34a] text-black font-display text-sm tracking-widest px-5 py-3 shadow-brutal hover:bg-white disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
      >
        {busy ? "OPENING…" : (hasAccount ? "RESUME ONBOARDING ▸" : "CONNECT STRIPE ▸")}
      </button>
    </div>
  );
}
